import React, { useState } from 'react'
import Tippy from '@tippyjs/react'
import PlusIcon from '@heroicons/react/20/solid/PlusIcon'
import XMarkIcon from "@heroicons/react/24/outline/XMarkIcon";
import {Field, FieldsManager} from "../../../../data/services/fields";
import {classNames} from "../../../util/util-helpers";
import Resources from "../../../../data/services/resources";
import {fieldsToHtml} from "../../../util/util-fields";

const TASK_STATUS_CONFIG = {
    1: { label: 'Open',        className: 'bg-tm-gray-100 text-tm-gray-600' },
    2: { label: 'In Progress', className: 'bg-sky-100 text-sky-700' },
    3: { label: 'Completed',   className: 'bg-emerald-100 text-emerald-700' },
    4: { label: 'Closed',      className: 'bg-violet-100 text-violet-700' },
}

const LinkedTasks = (props) => {
    const {
        data,
        handleUnlinkIssue,
        handleUpdateDataValues,
        translate,
    } = props

    const LinkTypeID = {
        1: translate('text.RelatesTo'),
        2: translate('text.BlockedBy'),
        3: translate('text.Duplicates'),
    }

    const getLinkedTypeColor = (type) => {
        switch (Number(type)) {
            case 1: return '#7c68ed'
            case 2: return '#fc991f'
            default: return '#4bade8'
        }
    }

    const getFields = () => {
        return {
            LinkTypeID: new Field('LinkTypeID', 1, ['empty'], false, 'select', {
                addContainerClass: 'w-36 flex-none',
                formLabelClass: 'hidden',
            }, { menuPlacement: 'top' }),
            LinkedTaskID: new Field('LinkedTaskID', '', ['empty'], false, 'multi-select-search', {
                addContainerClass: 'flex-1 min-w-0',
                formLabelClass: 'hidden',
            }, {
                multi: true,
                menuPlacement: 'top'
            }),
        }
    }

    const [fields, setFields] = useState(getFields())

    const handleLinkClick = (url) => {
        if (url.includes('http')) {
            window.open(url, '_blank')
        } else {
            window.open(`${window.location.origin}${url}`, '_blank')
        }
    }

    const handleInputChange = (name, value) => {
        setFields(FieldsManager.updateField(fields, name, value))
    }

    const handleSave = () => {
        const fieldsUpdate = FieldsManager.validateFields(fields)
        setFields(fieldsUpdate)
        if (fieldsUpdate.LinkTypeID.value && fieldsUpdate.LinkedTaskID.value) {
            handleUpdateDataValues(fieldsUpdate)
            setFields(getFields())
        }
    }

    const returnLinkID = (item) => {
        return (+item.LinkedTaskID) === props.TaskID ? item.TaskID : item.LinkedTaskID
    }

    const returnTaskName = (item) => {
        if ((+item.LinkedTaskID) === props.TaskID) {
            return `${item.TaskName}${!item.__IsNew__ ? ` · #${item.TaskID}` : ''}`
        }
        return `${item.LinkedTaskName}${!item.__IsNew__ ? ` · #${item.LinkedTaskID}` : ''}`
    }

    const returnTaskRow = (item, index) => {
        const statusConfig = item.TaskStatusID ? TASK_STATUS_CONFIG[item.TaskStatusID] : null

        return (
            <li key={`${item.LinkedTaskID}-${item.LinkTypeID}-${index}`}
                className="flex items-center gap-3 px-3 py-2.5 hover:bg-tm-gray-50">
                <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ background: getLinkedTypeColor(item.LinkTypeID) }}
                />

                <button
                    onClick={() => handleLinkClick(`/all-tasks?LinkedTaskID=${returnLinkID(item)}`)}
                    className="min-w-0 flex-1 truncate text-left text-sm text-tm-gray-700 hover:text-primary hover:underline cursor-pointer"
                >
                    {returnTaskName(item)}
                </button>

                {statusConfig && (
                    <span className={classNames(
                        'shrink-0 rounded px-2 py-0.5 text-xs font-semibold uppercase tracking-wide',
                        statusConfig.className
                    )}>
                        {statusConfig.label}
                    </span>
                )}

                {handleUnlinkIssue && (
                    <Tippy content={<span>{translate('text.UnlinkIssue')} — {translate('btn.save')} to confirm</span>}>
                        <button
                            className="shrink-0 rounded p-0.5 text-tm-gray-400 hover:bg-tm-gray-100 hover:text-tm-gray-700 cursor-pointer"
                            aria-label="Unlink"
                            onClick={() => handleUnlinkIssue(item)}
                        >
                            <XMarkIcon className="w-4 h-4"/>
                        </button>
                    </Tippy>
                )}
            </li>
        )
    }

    const selects = {
        LinkedTaskID: {
            api: 'api/' + Resources.Tasks,
            query: {
                limit: 20,
                offset: 0,
                sort: 'DESC',
                sortBy: 'TaskID',
                TaskInfo: 1,
                ExcludeTaskID: props.TaskID ?? 0
            },
            searchMap: (it) => ({
                label: `${it.TaskName} (#${it.TaskID})`,
                value: it.TaskID
            })
        },
        LinkTypeID: LinkTypeID
    }

    const fieldsHtml = fieldsToHtml(Object.values(Object.assign({}, fields)), translate, handleInputChange, selects)

    const readTypes = Object.assign({}, LinkTypeID, { 4: translate('text.Blocks') })

    const groups = Object.keys(readTypes)
        .map(typeKey => {
            const items = data.filter(item => (+item.LinkTypeID) === (+typeKey))
            if (!items.length) return null
            return (
                <div key={typeKey} className="space-y-1">
                    <p className="text-xs font-medium text-tm-gray-500">{readTypes[typeKey]}</p>
                    <ul className="divide-y divide-tm-gray-100 rounded-md border border-tm-gray-200">
                        {items.map((item, i) => returnTaskRow(item, i))}
                    </ul>
                </div>
            )
        })
        .filter(Boolean)

    return (
        <div className="space-y-3">
            {groups.length > 0 && (
                <div className="space-y-3">
                    {groups}
                </div>
            )}

            {!props.disableCreate && (
                <div className="flex gap-2 items-center">
                    {fieldsHtml}

                    <Tippy content={<span>{translate('btn.link_issue_save_note')}</span>}>
                        <button
                            className="btn-icon flex-none rounded-md border border-tm-gray-300 p-1.5 text-tm-gray-500 hover:bg-tm-gray-100 hover:text-tm-gray-700 disabled:opacity-40 cursor-pointer"
                            onClick={fields.LinkedTaskID.value ? handleSave : undefined}
                            disabled={!fields.LinkedTaskID.value}
                            aria-label={translate('text.Add')}
                        >
                            <PlusIcon className="w-5 h-5"/>
                        </button>
                    </Tippy>
                </div>
            )}
        </div>
    )
}

export default LinkedTasks
